import { createContext, useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { VideoCallPopup } from '../../components/VideoCallPopup/VideoCallPopup';
import { Chat, VideoCall } from '../../types/chat.types';
import { isChatStateProps } from '../../utils/chat.utils';
import { useChatSocketContext } from '../chatSocket/hooks/useChatSocketContext';
import {
  ChatContextProps,
  ChatContextProviderProps,
  HandleSubmitMessageAcceptedTypes,
  HandleSubmitMessageParams,
} from './chat.context.types';
import useChatViewStateHandler from './hooks/useChatViewStateHandler/useChatViewStateHandler';
import { GroupedChatRoomsState } from './hooks/useChats/reducer/chatListReducer.types';
import useChats from './hooks/useChats/useChats';
import useHandleWithSocketState from './hooks/useHandleWithSocketState/useHandleWithSocketState';
import useVideoCallHandler from './hooks/useVideoCallHandler/useVideoCallHandler';
import { chatInitialState, chatReducer } from './reducer/chatReducer';
import {
  addVideoCallInActiveChat,
  deleteVideoCall,
  resetState,
  updateSelectedChat,
} from './reducer/chatReducer.actions';
import {
  ChatViewType,
  isOnGroupedRooms,
  isRoomsOfGroupChatView,
} from './reducer/chatReducer.types';

export const ChatContext = createContext<ChatContextProps>({
  view: {
    type: ChatViewType.GroupedRooms,
  },
  rooms: [],
  isLoading: false,
  minimizedChatIds: [],
  hasErrorInConnectionToWs: false,
  loadMore: () => undefined,
  onSubmitMessage: async () => undefined,
  goToGroupedRooms: () => undefined,
  closeActiveChat: () => undefined,
  minimizeActiveChat: () => undefined,
  onClickCreateChat: () => undefined,
  onClickDeleteVideoCall: () => undefined,
  getLoadedChatById: () => undefined,
  removeChatFromMinimized: () => undefined,
  goToChatListOfGroup: () => undefined,
  getTotalUnreadMessagesCount: () => 0,
  getTotalUnreadMessageToChat: () => 0,
  updateChatWithNewInfo: () => undefined,
  onChangeSelectedChat: async () => undefined,
  onClickCreateGroupChat: () => undefined,
  onClickOpenPrivateChat: () => undefined,
  fetchChatsFromGroup: async () => undefined,
  addVideoCallInActiveChat: () => undefined,
  onClickJoinVideoCall: async () => undefined,
  onClickStartVideoCall: async () => undefined,
  updateLastViewedMessageInChat: () => undefined,
  openChatOfCaseOrCreate: async () => undefined,
  getCountOfChatsWithNotificationsByChatIds: () => 0,
  openPrivateChatBetweenStoreAndBrandOrChatCreation: async () => undefined,
});

export const ChatContextProvider = ({ children }: ChatContextProviderProps) => {
  const {
    team,
    state: { user },
  } = useApplicationContext();

  const [selectedGroupOfRooms, setSelectedGroupOfRooms] = useState<
    GroupedChatRoomsState | undefined
  >(undefined);

  const [state, dispatch] = useReducer(chatReducer, chatInitialState());

  const chatSocket = useChatSocketContext(
    isChatStateProps(state.activeChat) ? { selectedChat: state.activeChat } : undefined,
  );

  const chatsListState = useChats();
  useHandleWithSocketState({ chatsListState, state, dispatch });
  const chatViewsStateHandler = useChatViewStateHandler({ dispatch, chatsListState, state });
  const videoCallHandler = useVideoCallHandler({
    state,
    chatsListState,
    changeSelectedChat: chatViewsStateHandler.changeSelectedChat,
  });

  const updateChatListWithNewInfoAndState = useCallback(
    (chat: Chat) => {
      chatsListState.updateChatWithNewInfo(chat);
      if (state.activeChat?.id === chat.id) {
        dispatch(updateSelectedChat({ chat: { ...state.activeChat, ...chat, wasRemoved: false } }));
      }
    },
    [chatsListState.updateChatWithNewInfo, state.activeChat],
  );

  const handleAddVideoCallInActiveChat = useCallback((videoCall: VideoCall) => {
    dispatch(addVideoCallInActiveChat({ videoCall }));
  }, []);

  const handleDeleteVideoCall = useCallback(async (chatId?: string) => {
    const status = await videoCallHandler.handleDeleteVideoCall(chatId);

    if (status && !status.ok) {
      return;
    }

    dispatch(deleteVideoCall());
  }, []);

  const handleSubmitMessage = async <T extends HandleSubmitMessageAcceptedTypes>({
    data,
    text,
    type,
  }: HandleSubmitMessageParams<T>) => {
    if (!state.activeChat || !user) {
      return;
    }

    const currentChat = state.activeChat.id
      ? state.activeChat
      : await chatsListState.createChatIfNotExists({
          type: state.activeChat.type,
          caseId: state.activeChat.caseId,
          id: state.activeChat.id,
          title: state.activeChat.title,
          participants: {
            stores: state.activeChat.stores,
            brand: state.activeChat.brand,
          },
        });

    if (!currentChat?.id) {
      return;
    }

    if (!state.activeChat.id) {
      await chatViewsStateHandler.asyncChangeSelectedChat(currentChat.id);
    }

    chatSocket.submitMessage({
      chat: currentChat as Chat,
      message: {
        type,
        data,
        body: text,
        chatId: currentChat.id as string,
        tempId: uuidv4(),
      },
    });
  };

  const getSelectedGroupOfRooms = () => {
    const currentView = state.view;
    if (isRoomsOfGroupChatView(currentView)) {
      return chatsListState.state.chatRooms.find((room) => room.metaId === currentView.groupMetaId);
    }
  };

  useEffect(() => {
    const selectedGroupOfRoomsBasedInView = getSelectedGroupOfRooms();
    const currentView = state.view;

    if (selectedGroupOfRooms && isOnGroupedRooms(currentView)) {
      setSelectedGroupOfRooms(undefined);
      return;
    }

    if (
      selectedGroupOfRoomsBasedInView &&
      selectedGroupOfRoomsBasedInView !== selectedGroupOfRooms
    ) {
      setSelectedGroupOfRooms(selectedGroupOfRoomsBasedInView);
    }
  }, [state.view, chatsListState.state.chatRooms]);

  useEffect(() => {
    if (videoCallHandler.videoCall) {
      chatsListState.handleAddVideoCallInRoom(
        videoCallHandler.videoCall.chat.id,
        videoCallHandler.videoCall,
      );
      if (state.activeChat?.id === videoCallHandler.videoCall.chat.id) {
        handleAddVideoCallInActiveChat(videoCallHandler.videoCall);
      }
    }
  }, [videoCallHandler.videoCall]);

  useEffect(() => {
    if (!team) {
      return;
    }

    return () => {
      dispatch(resetState());
    };
  }, [team?.id]);

  useEffect(() => {
    if (chatSocket.errorRecoveringStateAfterDisconnect.hasErrorRecoveringState) {
      chatsListState.resetAllStates();
    }
  }, [chatSocket.errorRecoveringStateAfterDisconnect]);

  const values: ChatContextProps = {
    selectedGroupOfRooms,
    view: state.view,
    activeChat: state.activeChat,
    videoCall: videoCallHandler.videoCall,
    rooms: chatsListState.state.chatRooms,
    minimizedChatIds: state.minimizedChatIds,
    isLoading: chatsListState.state.isLoading,
    hasErrorInConnectionToWs: state.hasErrorInConnectionToWs,
    onSubmitMessage: handleSubmitMessage,
    loadMore: chatsListState.loadMoreRows,
    getLoadedChatById: chatsListState.getLoadedChatById,
    closeActiveChat: chatViewsStateHandler.closeActiveChat,
    fetchChatsFromGroup: chatsListState.fetchChatsFromGroup,
    updateChatWithNewInfo: updateChatListWithNewInfoAndState,
    goToGroupedRooms: chatViewsStateHandler.goToGroupedRooms,
    addVideoCallInActiveChat: handleAddVideoCallInActiveChat,
    onClickCreateChat: chatViewsStateHandler.openChatCreation,
    onClickJoinVideoCall: videoCallHandler.handleJoinVideoCall,
    onClickStartVideoCall: videoCallHandler.handleStartVideoCall,
    onClickDeleteVideoCall: handleDeleteVideoCall,
    minimizeActiveChat: chatViewsStateHandler.minimizeActiveChat,
    onClickOpenPrivateChat: chatViewsStateHandler.openPrivateChat,
    onChangeSelectedChat: chatViewsStateHandler.changeSelectedChat,
    goToChatListOfGroup: chatViewsStateHandler.goToChatListOfGroup,
    onClickCreateGroupChat: chatViewsStateHandler.openGroupChatCreation,
    openChatOfCaseOrCreate: chatViewsStateHandler.openChatOfCaseOrCreate,
    removeChatFromMinimized: chatViewsStateHandler.removeChatFromMinimized,
    getTotalUnreadMessageToChat: chatsListState.getTotalUnreadMessageToChat,
    getTotalUnreadMessagesCount: chatsListState.getTotalUnreadMessagesCount,
    updateLastViewedMessageInChat: chatsListState.handleUpdateLastViewedMessageInChat,
    openPrivateChatBetweenStoreAndBrandOrChatCreation:
      chatViewsStateHandler.openPrivateChatBetweenStoreAndBrandOrChatCreation,
    getCountOfChatsWithNotificationsByChatIds:
      chatsListState.getCountOfChatsWithNotificationsByChatIds,
  };

  return (
    <ChatContext.Provider value={values}>
      {children}

      {videoCallHandler.videoCall && (
        <VideoCallPopup
          room={videoCallHandler.videoCall.roomUrl}
          onLeave={videoCallHandler.handleLeaveVideoCall}
        />
      )}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => useContext<ChatContextProps>(ChatContext);
